import React, { useCallback, useEffect, useState, useMemo } from 'react';
import {
  getInitialSelectedIndicatorList,
  IndicatorByService,
  IndicatorSectionKey,
  indicatorSectionsInOrder,
  indicatorStageHeaderList,
  IndicatorStagesProps,
  IndicatorWithUpdate,
} from './utils';
import { useClient } from 'shared/apollo';
import { ApolloClient } from 'apollo-boost';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { UpdateReportResponse, updateIndicatorAnswer } from './api';
import { IndicatorStageRenderProps } from './IndicatorStageRender';
import { updateBulkAnswer } from '../../../NPOFeedback/MakeEdits/api';
import { GuidanceTextResponse } from '../utils';
import { REACH_QUESTION_ID } from '../SectionMultiRadio';
import { getModelBulkAnswers } from 'shared/providers/IndicatorProvider/utils';

const CAPACITY_DEVELOPMENT_QUESTION_ID = 40021;
export const LEARN_QUESTION_ID = 40007;
export const ACT_QUESTION_ID = 40008;
const IMPACT_SUMMARY_QUESTION_ID = 40015;
const SUCCEED_QUESTION_ID = 40010;

const hasIndicatorsChanged = (
  i1: IndicatorByService,
  i2: IndicatorByService,
) => {
  for (const element of indicatorSectionsInOrder) {
    if (i1[element].length !== i2[element].length) {
      return true;
    }
  }
  return false;
};

interface SectionIndicatorStageProps extends IndicatorStagesProps {
  maxWidth: number;
  guidanceText: GuidanceTextResponse | undefined;
  defaultIndicators: IndicatorByService;
  setReportBuilderValues?: React.Dispatch<
    React.SetStateAction<{ [id: number]: string | undefined }>
  >;
  handleDetailOpen: () => unknown;
  handleDetailClose: () => unknown;
  RenderComponent: React.FC<IndicatorStageRenderProps>;
  sectionAnswers?: { dbId: number; text: string; questionId: number }[];
}

const savePreviousQuestion = async ({
  client,
  reportId,
  selectedIndicators,
  setReportBuilderValues,
  sectionAnswers,
}: {
  selectedIndicators: IndicatorByService;
  client: ApolloClient<object>;
  reportId: number;
  setReportBuilderValues: SectionIndicatorStageProps['setReportBuilderValues'];
  sectionAnswers?: { dbId: number; text: string; questionId: number }[];
}) => {
  const result = getModelBulkAnswers(selectedIndicators, undefined, 'INITIAL');
  /*
    This code ensures that any extra indicator added to 40010 (succeed) is included.
    The problem is that the "add indicator not listed here" feature allows users to
    add indicators at any stage. As a result, the logic in this component is unable
    to detect an indicator that is not present in the expected stage. When performing
    the bulk save, it simply gets ignored. The logic below checks for 40010's existing
    answers from the previous step, verifies if any of those indicators are included in
    any of the stages, and adds them to the 40010 bulk save response if present in any other stage.
  */
  if (sectionAnswers) {
    try {
      const allIndicators = [
        ...selectedIndicators.capacityDevelopment.map(x => x.name),
        ...selectedIndicators.reach.map(x => x.name),
        ...selectedIndicators.learn.map(x => x.name),
        ...selectedIndicators.act.map(x => x.name),
        ...selectedIndicators.succeed.map(x => x.name),
        ...selectedIndicators.catalyzed.map(x => x.name),
      ];
      const succeedPrevAnswers = sectionAnswers.filter(
        ans => ans.questionId === SUCCEED_QUESTION_ID,
      )?.[0]?.text;
      if (succeedPrevAnswers) {
        const loadPrevAnswers = JSON.parse(succeedPrevAnswers);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        loadPrevAnswers.forEach((prevAnswer: any) => {
          if (
            allIndicators.includes(prevAnswer) &&
            !result.succeedAnswerArray.includes(prevAnswer)
          ) {
            result.succeedAnswerArray.push(prevAnswer);
          }
        });
      }
    } catch (_ex) { /* empty */ }
  }
  updateBulkAnswer(client, reportId, result.result);
  setReportBuilderValues?.(s => {
    const newState = { ...s };
    newState[CAPACITY_DEVELOPMENT_QUESTION_ID] =
      result.capacityDevelopmentAnswer;
    newState[REACH_QUESTION_ID] = result.reachAnswer;
    newState[LEARN_QUESTION_ID] = result.learnAnswer;
    newState[ACT_QUESTION_ID] = result.actAnswer;
    newState[SUCCEED_QUESTION_ID] = result.succeedAnswer;
    newState[IMPACT_SUMMARY_QUESTION_ID] = result.indicatorSummaryAnswer;
    return newState;
  });
};

const defaultDeletedIndicators = {
  act: [],
  capacityDevelopment: [],
  catalyzed: [],
  learn: [],
  reach: [],
  succeed: [],
};

const SectionIndicatorStage: React.FC<SectionIndicatorStageProps> = ({
  isDebug,
  defaultIndicators,
  service,
  services,
  serviceType,
  guidanceText,
  capacityDevelopmentEnabled,
  learnEnabled,
  actEnabled,
  questionId,
  reportId,
  title,
  maxWidth,
  indicatorSummary,
  beneficiaryTerm,
  programName,
  intermediaries,
  serviceGoal,
  reportPeriod,
  userAnswerId,
  choiceIsSelected,
  pageSectionProps,
  RenderComponent,
  isReportPassed,
  isImpactSummaryCompleted,
  reloadAnswers,
  handleBack,
  handleNext,
  handleChange,
  setReportBuilderValues,
  handleDetailOpen,
  handleDetailClose,
  sectionAnswers,
}) => {
  const client = useClient();
  const [mounted, setMounted] = useState<boolean>(false);
  const [answerId, setAnswerId] = useState<number | undefined>(userAnswerId);
  const [updateAnswerRequest, makeUpdateAnswerRequest] = useGraphQLRequest<
    UpdateReportResponse
  >(undefined, true);

  useEffect(() => {
    if (updateAnswerRequest.type === 'REQUEST_SUCCESS') {
      setAnswerId(updateAnswerRequest.data.updateNpoAnswer.id);
    }
  }, [updateAnswerRequest]);

  const { indicatorHeaderList, firstSectionKey } = useMemo(() => {
    const indicatorHeaderList = indicatorStageHeaderList(
      defaultIndicators,
      capacityDevelopmentEnabled,
      learnEnabled,
      actEnabled,
    );
    return {
      indicatorHeaderList,
      firstSectionKey: indicatorHeaderList[0].sectionKey,
    };
  }, [defaultIndicators, learnEnabled, actEnabled]);

  const { selected, rest } = useMemo(() => {
    return getInitialSelectedIndicatorList(
      indicatorSummary,
      defaultIndicators,
      capacityDevelopmentEnabled,
      learnEnabled,
      actEnabled,
      firstSectionKey,
    );
  }, [indicatorSummary]);

  const [unselectedIndicators, setUnselectedIndicators] = useState<
    IndicatorByService
  >(rest);

  const [deletedIndicators, setDeletedIndicators] = useState<
    IndicatorByService
  >(defaultDeletedIndicators);

  const [selectedIndicators, setSelectedIndicators] = useState<
    IndicatorByService
  >(selected);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).getIndicatorId = () => selectedIndicators;

  const updatePreviousAnswers = useCallback(() => {
    savePreviousQuestion({
      client: client!,
      reportId,
      selectedIndicators,
      setReportBuilderValues,
      sectionAnswers,
    });
  }, [selectedIndicators, setReportBuilderValues, sectionAnswers]);

  const goBack = useCallback(() => {
    updatePreviousAnswers();
    handleBack();
  }, [handleBack, updatePreviousAnswers]);

  // Removing because of TIPD-4080
  // TIPD-3818
  // useEffect(() => {
  //   if (hasIndicatorsChanged(selected, selectedIndicators)) {
  //     setSelectedIndicators(selected);
  //   }
  // }, [selected]);

  const goNext = useCallback(
    (
      pressedButton?: boolean | undefined,
      forcedValue?: string | string[] | undefined,
    ) => {
      updatePreviousAnswers();
      handleNext(pressedButton, forcedValue);
    },
    [handleNext, updatePreviousAnswers],
  );

  useEffect(() => {
    if (!mounted) {
      setMounted(true);
      return;
    }
    makeUpdateAnswerRequest(
      updateIndicatorAnswer(
        client!,
        answerId,
        true,
        questionId,
        reportId,
        JSON.stringify(selectedIndicators),
      ),
    );
  }, [selectedIndicators, setMounted]);

  const handleSaveAndContinue = useCallback(() => {
    const answer = JSON.stringify(selectedIndicators);
    handleChange(answer); // this is used for final updates and feedback
    handleNext(true, answer);
    updatePreviousAnswers();
  }, [selectedIndicators, updatePreviousAnswers, handleChange, handleNext]);

  const handleGoToReview = useCallback(() => {
    updatePreviousAnswers();
    handleDetailOpen();
  }, [updatePreviousAnswers, handleDetailOpen]);

  const handleIndicatorTextUpdate = useCallback(
    (key: IndicatorSectionKey, text: string, index: number) => {
      setSelectedIndicators(s => {
        const newState = { ...s };
        newState[key][index].seen = true;
        newState[key][index].updatedText = text;
        return newState;
      });
    },
    [setSelectedIndicators],
  );

  const handleIndicatorSeenUpdate = useCallback(
    (key: IndicatorSectionKey, seen: boolean, index: number) => {
      setSelectedIndicators(s => {
        const newState = { ...s };
        newState[key][index].seen = seen;
        return newState;
      });
    },
    [setSelectedIndicators],
  );

  const handleAddIndicator = useCallback(
    (
      sectionKey: IndicatorSectionKey,
      indicator: IndicatorWithUpdate,
      index: number,
    ) => {
      setSelectedIndicators(s => ({
        ...s,
        [sectionKey]: [...s[sectionKey], indicator],
      }));
      setUnselectedIndicators(s => {
        const newState = { ...s };
        const list = newState[sectionKey];
        list.splice(index, 1);
        return newState;
      });
    },
    [setUnselectedIndicators, setSelectedIndicators],
  );

  const handleRestoreIndicator = useCallback(
    (
      sectionKey: IndicatorSectionKey,
      indicator: IndicatorWithUpdate,
      index: number,
    ) => {
      setDeletedIndicators(s => {
        const newState = { ...s };
        const list = newState[sectionKey];
        list.splice(index, 1);
        return newState;
      });
      setSelectedIndicators(s => ({
        ...s,
        [sectionKey]: [...s[sectionKey], indicator],
      }));
    },
    [unselectedIndicators, setDeletedIndicators],
  );

  const handleDeleteIndicator = useCallback(
    (
      sectionKey: IndicatorSectionKey,
      indicator: IndicatorWithUpdate,
      index: number,
    ) => {
      setDeletedIndicators(s => ({
        ...s,
        [sectionKey]: [...s[sectionKey], indicator],
      }));
      setSelectedIndicators(s => {
        const newState = { ...s };
        const list = newState[sectionKey];
        list.splice(index, 1);
        return newState;
      });
    },
    [setSelectedIndicators, setDeletedIndicators],
  );

  return (
    <RenderComponent
      isDebug={isDebug}
      maxWidth={maxWidth}
      title={title}
      guidanceText={guidanceText}
      capacityDevelopmentEnabled={capacityDevelopmentEnabled}
      learnEnabled={learnEnabled}
      actEnabled={actEnabled}
      service={service}
      services={services}
      beneficiaryTerm={beneficiaryTerm}
      programName={programName}
      intermediaries={intermediaries}
      serviceGoal={serviceGoal}
      serviceType={serviceType}
      reportPeriod={reportPeriod}
      reportId={reportId}
      questionId={questionId}
      firstSectionKey={firstSectionKey}
      defaultIndicators={defaultIndicators}
      unselectedIndicators={unselectedIndicators}
      selectedIndicators={selectedIndicators}
      choiceIsSelected={choiceIsSelected}
      pageSectionProps={pageSectionProps}
      indicatorHeaderList={indicatorHeaderList}
      isReportPassed={isReportPassed}
      handleBack={goBack}
      handleNext={goNext}
      handleSaveAndContinue={handleSaveAndContinue}
      handleDetailOpen={handleGoToReview}
      handleDetailClose={handleDetailClose}
      handleIndicatorTextUpdate={handleIndicatorTextUpdate}
      handleIndicatorSeenUpdate={handleIndicatorSeenUpdate}
      handleAddIndicator={handleAddIndicator}
      handleDeleteIndicator={handleDeleteIndicator}
      handleRestoreIndicator={handleRestoreIndicator}
      deletedIndicators={deletedIndicators}
      isImpactSummaryCompleted={isImpactSummaryCompleted}
    />
  );
};

export default SectionIndicatorStage;
